'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SearchSuggestion {
  id: string;
  name: string;
  category: string;
  type: 'product' | 'category';
}

interface SearchBarProps {
  onSearch: (query: string) => void;
  suggestions: SearchSuggestion[];
}

export default function SearchBar({ onSearch, suggestions }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setShowSuggestions(value.length > 0);
    setFocusedIndex(-1);
  };

  const handleSearch = (searchQuery: string) => {
    onSearch(searchQuery);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0) {
        handleSearch(suggestions[focusedIndex].name);
        setQuery(suggestions[focusedIndex].name);
      } else {
        handleSearch(query);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const filteredSuggestions = suggestions.filter((suggestion) =>
    suggestion.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length > 0 && setShowSuggestions(true)}
          placeholder="Search by equipment name, model, or manufacturer..."
          className="w-full pl-12 pr-12 py-4 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-text-primary placeholder-text-secondary"
        />
        <Icon
          name="MagnifyingGlassIcon"
          size={24}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setShowSuggestions(false);
              onSearch('');
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors duration-250"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        )}
      </div>

      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
          <div className="p-2">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                onClick={() => {
                  handleSearch(suggestion.name);
                  setQuery(suggestion.name);
                }}
                className={`w-full text-left px-4 py-3 rounded-md transition-colors duration-250 ${
                  index === focusedIndex
                    ? 'bg-muted' :'hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon
                    name={suggestion.type === 'product' ? 'CubeIcon' : 'FolderIcon'}
                    size={20}
                    className="text-text-secondary"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-text-primary">{suggestion.name}</p>
                    <p className="text-xs text-text-secondary">{suggestion.category}</p>
                  </div>
                  <Icon name="ArrowRightIcon" size={16} className="text-text-secondary" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}